/**
 * The excerpt of a redlined package that a narrow view draws: only the
 * paragraphs and table rows that hold a change, and in each such paragraph only
 * the few words of unchanged text nearest each change, with a mark where text
 * was left out.
 *
 * WHY a cut of the markup, not of the text
 *   The drawer's row, a conflict option and a collision hover draw a card's
 *   change from its live proposal package, the markup the chat card draws and
 *   Apply writes, through the one renderer (`ooxmlToHtml`). Cutting the parsed
 *   package keeps every run, revision, tab and break the renderer draws, so an
 *   excerpt reads exactly as the chat card reads, only shorter.
 *
 * HOW
 *   - a change is text inside one of the card's own revisions: a revision by
 *     one of the excerpt's change authors (the card's proposal, relabelled
 *     "Proposed by this redline"), or one stamped with one of its change stamps
 *     (the user's edit of the card, stamped after the proposal); every other
 *     revision, an earlier card's or the counterparty's, is drawn as it stands
 *     but counts as unchanged text;
 *   - unchanged text is counted in the engine's word units
 *     (`WordTokenizer.diffUnits`), so a Japanese clause keeps its nearest
 *     words, not its nearest runs between spaces;
 *   - where text is cut, a gap element (`CHANGE_EXCERPT_GAP`) takes its place,
 *     and the renderer draws it as "…";
 *   - the elements are matched by their qualified names ("w:p", "w:ins"), as
 *     the renderer matches them, so the cut and the drawing see one document.
 */
import { Revision } from "../ooxml/revision";
import { WordTokenizer } from "./word-tokenizer";

/** What an excerpt keeps of a redlined package. */
export interface ChangeExcerptOptions {
    /** How many engine word units of unchanged text are kept on each side of a change. */
    contextUnits: number;
    /** Authors whose revisions are the change the excerpt is cut around. */
    changeAuthors: readonly string[];
    /** `w:date` stamps whose revisions are the change too, whoever made them: an edit layer stamped apart from every earlier revision of its author. */
    changeStamps: readonly string[];
    /** True when Word's move marks are drawn as the deletion and insertion they are, so a move that is one of the changes is a change too. */
    countsMovesAsChanges: boolean;
    /** How many paragraphs or tables holding a change are drawn; the rest are counted in a "more" mark. Null draws them all. */
    blockLimit: number | null;
}

/**
 * What makes a revision one of the card's own changes, apart from how much of the
 * text around it is kept: the part of `ChangeExcerptOptions` every cut of a card's
 * package reads its changes by, the excerpt and a collapsed table's rows
 * (`TableRowsExcerpt`) alike.
 */
export type ExcerptChangeReading = Pick<ChangeExcerptOptions, "changeAuthors" | "changeStamps" | "countsMovesAsChanges">;

/** The namespace of the two marks the cut leaves for the renderer; no Word document holds it. */
export const CHANGE_EXCERPT_NAMESPACE = "urn:dioptra:change-excerpt";

/** The element standing where unchanged text was left out; the renderer draws "…". */
export const CHANGE_EXCERPT_GAP = "dx:gap";

/** The element standing after the last block drawn when `blockLimit` left blocks out; its count attribute says how many. */
export const CHANGE_EXCERPT_MORE = "dx:more";

/** The attribute of `CHANGE_EXCERPT_MORE` that holds how many blocks were left out. */
export const CHANGE_EXCERPT_MORE_COUNT = "dx:count";

/**
 * The element standing at the end of a paragraph whose only change is its
 * paragraph mark; the renderer draws "¶" as that revision. It carries the
 * revision's `w:author` and `w:date`, and `CHANGE_EXCERPT_PARAGRAPH_MARK_KIND`
 * says whether the mark was inserted or deleted.
 */
export const CHANGE_EXCERPT_PARAGRAPH_MARK = "dx:paragraphMark";

/** The attribute of `CHANGE_EXCERPT_PARAGRAPH_MARK` that holds "inserted" or "deleted". */
export const CHANGE_EXCERPT_PARAGRAPH_MARK_KIND = "dx:kind";

/** Subtrees the renderer draws nothing for: properties and field instructions. Their text is never part of an excerpt. */
const SUBTREES_DRAWN_AS_NOTHING = new Set(["w:pPr", "w:rPr", "w:trPr", "w:tcPr", "w:tblPr", "w:tblGrid", "w:instrText"]);

/** The elements whose text nodes the renderer draws. */
const TEXT_BEARING_ELEMENTS = new Set(["w:t", "w:delText", "m:t"]);

const REVISION_ELEMENTS = new Set(["w:ins", "w:del"]);

const MOVE_ELEMENTS = new Set(["w:moveFrom", "w:moveTo"]);

/** The elements drawn as a mark of their own: a tab spacer or a line break. */
const DRAWN_MARK_ELEMENTS = new Set(["w:tab", "w:br", "w:cr"]);

/** Wrappers that a cut can leave holding nothing to draw. */
const WRAPPERS_EMPTIED_BY_A_CUT = new Set(["w:r", "w:ins", "w:del", "w:moveFrom", "w:moveTo", "w:hyperlink", "w:smartTag", "w:t", "w:delText"]);

const DOCUMENT_PART_NAME = "/word/document.xml";

/** `Node.ELEMENT_NODE` and `Node.TEXT_NODE`, named here because a server's XML host has no `Node` global. */
const ELEMENT_NODE_TYPE = 1;
const TEXT_NODE_TYPE = 3;

/**
 * One thing the renderer draws inside a paragraph, in reading order: a text
 * node's characters, or a tab or line break element read as the one character
 * it stands for.
 */
interface DrawnItem {
    node: Node;
    text: string;
    isMark: boolean;
    changed: boolean;
}

/** A run of drawn items that are all changes, or all unchanged text. */
interface DrawnStretch {
    items: DrawnItem[];
    changed: boolean;
}

/** Where an unchanged stretch stands among the paragraph's changes. */
type StretchPlace = "beforeFirstChange" | "afterLastChange" | "betweenChanges";

/** A character range `[startOffset, endOffset)` of a stretch's text that the excerpt keeps. */
interface KeptRange {
    startOffset: number;
    endOffset: number;
}

/**
 * What the excerpt keeps of one unchanged stretch: the kept ranges, and where a
 * gap mark stands for the text cut out.
 *
 * CASES, for the gap
 *   - "paragraphStart"       -> the cut text led the paragraph: the gap opens the paragraph
 *   - "paragraphEnd"         -> the cut text ended the paragraph: the gap closes it
 *   - an offset              -> the cut text stood between two changes: the gap stands at that
 *                               offset of the stretch's text, where the cut text began
 *   - null                   -> nothing was cut
 */
interface StretchCut {
    keptRanges: KeptRange[];
    gap: "paragraphStart" | "paragraphEnd" | number | null;
}

export class ChangeExcerpt {
    /**
     * Cuts the parsed package down to its excerpt, in place.
     *
     * CASES, per paragraph of the document part
     *   - it holds a change, anywhere                 -> kept, its unchanged text cut to
     *                                                    `contextUnits` units around each change
     *   - it holds none and stands outside any table  -> left out
     *   - it holds none and stands in a table cell    -> kept whole when its row is kept, so
     *                                                    the row still reads as a row
     *   - its text holds none and its row is one the  -> kept whole, its mark drawn as no ¶: it
     *     card inserts or deletes                        holds the row's change, which the row's
     *                                                    tint draws
     *
     * CASES, per table
     *   - no paragraph of it holds a change           -> left out
     *   - otherwise                                   -> only its rows that hold a change are kept
     *
     * CASES, for a paragraph drawn on its own (one text's redline, `TextChanges.redlinedParagraphXml`)
     *   - it holds a change                           -> its unchanged text is cut like any paragraph's
     *   - it holds none                               -> drawn whole: it is everything there is to draw
     */
    static cut(document: Document, options: ChangeExcerptOptions): void {
        const root = ChangeExcerpt.documentPartRootOf(document);
        const paragraphsUnderRoot = Array.from(root.getElementsByTagName("w:p"));
        /**
         * WHEN the parsed XML is one paragraph, `<w:p xmlns:w=…>`, so the paragraph is the root
         *   CONSEQUENCE
         *     `getElementsByTagName` finds only the elements under the root, never the root itself,
         *     so the root paragraph is put first in the list to cut.
         */
        const paragraphs = root.nodeName === "w:p" ? [root, ...paragraphsUnderRoot] : paragraphsUnderRoot;
        const paragraphsHoldingChanges = new Set<Element>();
        for (const paragraph of paragraphs) {
            const items = ChangeExcerpt.drawnItemsOf(paragraph, options);
            /**
             * WHEN the paragraph's text holds no change and its row is one the card inserts or deletes
             *   CONSEQUENCE
             *     it holds the row's change, so the row is kept even when every cell of it is empty,
             *     and its mark, revised with the row, draws no ¶ beside the row's tint.
             *
             * EXAMPLE
             *   an empty answer cell in a row the card inserts -> the tinted row, the cell blank, no ¶
             */
            if (!items.some((item) => item.changed) && ChangeExcerpt.rowOfParagraphIsExcerptChange(paragraph, options)) {
                paragraphsHoldingChanges.add(paragraph);
                continue;
            }
            /**
             * WHEN the paragraph's text holds no change and its paragraph mark is one of the card's own revisions
             *   CONSEQUENCE
             *     the mark is drawn as a ¶ at the paragraph's end (`CHANGE_EXCERPT_PARAGRAPH_MARK`), so a
             *     change that is only a paragraph mark still shows: a split, a join, an empty paragraph
             *     added or removed. A paragraph whose text changed too draws no ¶; its text says enough.
             */
            if (!items.some((item) => item.changed)) {
                const paragraphMark = ChangeExcerpt.changedParagraphMarkOf(document, paragraph, options);
                if (paragraphMark !== null) items.push({ node: paragraphMark, text: "¶", isMark: true, changed: true });
            }
            /**
             * WHEN nothing in the paragraph is one of the card's own revisions
             *   CONSEQUENCE
             *     its text is not cut: it is left out below, or kept whole in a kept row.
             */
            if (!items.some((item) => item.changed)) continue;
            paragraphsHoldingChanges.add(paragraph);
            ChangeExcerpt.cutUnchangedText(document, paragraph, items, options.contextUnits);
        }
        /**
         * WHY only the paragraphs under the root
         *   The root paragraph is the whole drawing. Left out when it holds no change, the
         *   drawing would be empty, so it stays, cut or whole.
         */
        ChangeExcerpt.removeParagraphsOutsideTables(paragraphsUnderRoot, paragraphsHoldingChanges);
        ChangeExcerpt.removeTablesAndRowsWithoutChanges(root, paragraphsHoldingChanges);
        /**
         * CASES
         *   - a block limit is set  -> blocks after it are left out and counted in a "more" mark
         *   - none                  -> every block holding a change is drawn
         */
        if (options.blockLimit !== null) ChangeExcerpt.limitBlocks(document, root, options.blockLimit);
    }

    /**
     * The element the cut works under: the document part of a flat package, or
     * the whole document for a bare paragraph or body.
     *
     * WHY
     *   A package read from Word carries other parts (comments, styles) whose
     *   paragraphs the renderer never draws; counting or cutting them would put
     *   blocks in the excerpt that nobody sees.
     */
    static documentPartRootOf(document: Document): Element {
        const documentPart = Array.from(document.getElementsByTagName("pkg:part")).find((part) => part.getAttribute("pkg:name") === DOCUMENT_PART_NAME);
        return documentPart ?? document.documentElement;
    }

    /**
     * Everything the renderer draws inside one paragraph, in reading order, each
     * marked as a change or not.
     *
     * CASES, per node under the paragraph
     *   - a text node of `w:t`, `w:delText` or `m:t`     -> one item, its characters
     *   - `w:tab`                                        -> one item read as "\t"
     *   - `w:br` or `w:cr`                               -> one item read as "\n"
     *   - a properties element or field instruction     -> nothing: the renderer draws nothing for it
     *   - a paragraph nested inside (a text box's)       -> nothing here: it is cut as its own paragraph
     *   - any other element                              -> its children, marked as a change from here
     *                                                       down when it is one of the card's own revisions
     */
    private static drawnItemsOf(paragraph: Element, options: ExcerptChangeReading): DrawnItem[] {
        const items: DrawnItem[] = [];
        const visit = (node: Node, insideChange: boolean): void => {
            if (node.nodeType === TEXT_NODE_TYPE) {
                if (TEXT_BEARING_ELEMENTS.has(node.parentNode?.nodeName ?? "")) items.push({ node, text: node.nodeValue ?? "", isMark: false, changed: insideChange });
                return;
            }
            if (node.nodeType !== ELEMENT_NODE_TYPE) return;
            const element = node as Element;
            const name = element.nodeName;
            if (SUBTREES_DRAWN_AS_NOTHING.has(name)) return;
            if (name === "w:p" && element !== paragraph) return;
            if (name === "w:tab") {
                items.push({ node, text: "\t", isMark: true, changed: insideChange });
                return;
            }
            if (name === "w:br" || name === "w:cr") {
                items.push({ node, text: "\n", isMark: true, changed: insideChange });
                return;
            }
            const marksChange = insideChange || ChangeExcerpt.isExcerptChange(element, options);
            for (const child of Array.from(element.childNodes)) visit(child, marksChange);
        };
        visit(paragraph, false);
        return items;
    }

    /**
     * The ¶ element standing for the paragraph's mark, appended to the paragraph,
     * when the mark is one of the card's own revisions (`w:pPr/w:rPr/w:ins` or `w:del`).
     *
     * EXAMPLE
     *   a card splits "Fees are payable monthly. Interest accrues daily." after "monthly."
     *   the first paragraph's mark carries w:ins by the card -> "Fees are payable monthly.¶", the ¶ underlined
     *
     * CASES
     *   - the mark carries the card's insertion         -> an "inserted" ¶
     *   - the mark carries the card's deletion          -> a "deleted" ¶
     *   - it carries neither, or another revision       -> null: the mark is no change of this excerpt
     */
    private static changedParagraphMarkOf(document: Document, paragraph: Element, options: ChangeExcerptOptions): Element | null {
        const markRevision = ChangeExcerpt.changedParagraphMarkRevisionOf(paragraph, options);
        /** NULL: the paragraph's mark is no revision of the card's, so no ¶ is drawn for it. */
        if (markRevision === null) return null;
        const paragraphMark = document.createElementNS(CHANGE_EXCERPT_NAMESPACE, CHANGE_EXCERPT_PARAGRAPH_MARK);
        paragraphMark.setAttributeNS(CHANGE_EXCERPT_NAMESPACE, CHANGE_EXCERPT_PARAGRAPH_MARK_KIND, markRevision.nodeName === "w:ins" ? "inserted" : "deleted");
        paragraphMark.setAttribute("w:author", markRevision.getAttribute("w:author") ?? "");
        paragraphMark.setAttribute("w:date", markRevision.getAttribute("w:date") ?? "");
        paragraph.appendChild(paragraphMark);
        return paragraphMark;
    }

    /**
     * The card's own revision on the paragraph's mark (`w:pPr/w:rPr/w:ins` or
     * `w:del`), read without drawing anything for it.
     *
     * CASES
     *   - the mark carries the card's insertion or deletion -> that revision
     *   - no mark properties, or another author's revision  -> null
     */
    private static changedParagraphMarkRevisionOf(paragraph: Element, options: ExcerptChangeReading): Element | null {
        const paragraphProperties = ChangeExcerpt.childNamed(paragraph, "w:pPr");
        const markRunProperties = paragraphProperties === null ? null : ChangeExcerpt.childNamed(paragraphProperties, "w:rPr");
        /** WHEN the paragraph's mark has no run properties CONSEQUENCE it carries no revision at all. */
        if (markRunProperties === null) return null;
        const markRevision = [ChangeExcerpt.childNamed(markRunProperties, "w:ins"), ChangeExcerpt.childNamed(markRunProperties, "w:del")]
            .find((revision): revision is Element => revision !== null && ChangeExcerpt.isExcerptChange(revision, options));
        return markRevision ?? null;
    }

    /**
     * True when the paragraph holds one of the card's own changes, by the three
     * tests the cut keeps a paragraph by: changed text, its row inserted or deleted
     * by the card, or its mark revised by the card. Nothing in the document is
     * touched.
     *
     * WHY one test, here
     *   A collapsed table keeps the rows that hold a change (`TableRowsExcerpt`).
     *   Asking the excerpt's own question keeps one reading of what a card changed,
     *   so a row the drawer's excerpt keeps is a row the collapsed card keeps.
     *
     * EXAMPLE
     *   "Supplier" -> "Contractor" in a cell, the card's w:del and w:ins    -> true
     *   a cell of a row the card inserts, left empty                        -> true
     *   a header cell no revision of the card's touches                     -> false
     */
    static paragraphHoldsChange(paragraph: Element, options: ExcerptChangeReading): boolean {
        if (ChangeExcerpt.drawnItemsOf(paragraph, options).some((item) => item.changed)) return true;
        if (ChangeExcerpt.rowOfParagraphIsExcerptChange(paragraph, options)) return true;
        return ChangeExcerpt.changedParagraphMarkRevisionOf(paragraph, options) !== null;
    }

    /**
     * True when the paragraph's own row is one the card inserts or deletes: its
     * `w:trPr/w:ins` or `w:trPr/w:del` is one of the card's own revisions.
     *
     * WHY
     *   Word marks a whole-row change on the row and on every paragraph mark in it
     *   (`TableRowXml`), and the renderer tints the row for the first; drawn as a ¶
     *   too, the mark of an empty cell showed the one change twice.
     *
     * CASES
     *   - the paragraph stands outside every table           -> false
     *   - its row carries no row revision                    -> false
     *   - its row's revision is another author's             -> false: a split in it still draws ¶
     *   - its row's revision is the card's own               -> true
     */
    private static rowOfParagraphIsExcerptChange(paragraph: Element, options: ExcerptChangeReading): boolean {
        const row = ChangeExcerpt.ancestorNamed(paragraph, "w:tr");
        /** WHEN the paragraph stands outside every table CONSEQUENCE it has no row to be changed with. */
        if (row === null) return false;
        const rowProperties = ChangeExcerpt.childNamed(row as Element, "w:trPr");
        /** WHEN the row has no properties CONSEQUENCE it carries no row revision. */
        if (rowProperties === null) return false;
        return ["w:ins", "w:del"].some((revisionName) => {
            const rowRevision = ChangeExcerpt.childNamed(rowProperties, revisionName);
            return rowRevision !== null && ChangeExcerpt.isExcerptChange(rowRevision, options);
        });
    }

    /** The first child element of `element` with this qualified name, or null. */
    private static childNamed(element: Element, qualifiedName: string): Element | null {
        for (const child of Array.from(element.childNodes)) {
            if (child.nodeType === ELEMENT_NODE_TYPE && child.nodeName === qualifiedName) return child as Element;
        }
        return null;
    }

    /**
     * True when the element is one of the card's own revisions.
     *
     * CASES
     *   - `w:ins` or `w:del` by a change author, or stamped with a change stamp        -> true
     *   - `w:moveFrom` or `w:moveTo` the same way, moves drawn                         -> true
     *   - a revision by anyone else under any other stamp, or any other element        -> false
     *
     * WHY the stamps
     *   The user's edit of a card is written under the user's own name, and an earlier
     *   card the user applied carries that name too. Its stamp, one second after the
     *   proposal's, is what tells the edit apart, so an earlier card's change on the
     *   same paragraph reads as unchanged text and is never cut around.
     */
    private static isExcerptChange(element: Element, options: ExcerptChangeReading): boolean {
        const name = element.nodeName;
        const isRevision = REVISION_ELEMENTS.has(name) || (options.countsMovesAsChanges && MOVE_ELEMENTS.has(name));
        if (!isRevision) return false;
        const author = element.getAttribute("w:author");
        const stamp = element.getAttribute("w:date");
        return options.changeAuthors.some((changeAuthor) => Revision.sameAuthor(changeAuthor, author)) || (stamp !== null && options.changeStamps.includes(stamp));
    }

    /**
     * Cuts every unchanged stretch of one paragraph that holds a change.
     *
     * EXAMPLE, contextUnits 2
     *   items      "6.2 Customer shall pay within " | del "thirty" | ins "forty" | " days of receipt."
     *   stretches  unchanged (before the first change), changed, changed, unchanged (after the last)
     *   kept       "pay within " after a gap, and " days of" before a gap
     */
    private static cutUnchangedText(document: Document, paragraph: Element, items: DrawnItem[], contextUnits: number): void {
        const stretches = ChangeExcerpt.stretchesOf(items);
        const firstChangedIndex = stretches.findIndex((stretch) => stretch.changed);
        /**
         * EXAMPLE
         *   stretches  [unchanged, changed, unchanged, changed, unchanged]  (length 5)
         *   reversed   [unchanged, changed, unchanged, changed, unchanged]
         *   findIndex on the reversed list -> 1, so the last changed stretch is 5 - 1 - 1 = 3
         */
        const lastChangedIndex = stretches.length - 1 - [...stretches].reverse().findIndex((stretch) => stretch.changed);
        stretches.forEach((stretch, index) => {
            if (stretch.changed) return;
            /**
             * CASES
             *   - before the first change -> keeps the words nearest the change, the end of the stretch
             *   - after the last change   -> keeps the words nearest the change, the start of the stretch
             *   - between two changes     -> keeps both ends, or all of it when it is short
             */
            const place: StretchPlace = index < firstChangedIndex ? "beforeFirstChange" : index > lastChangedIndex ? "afterLastChange" : "betweenChanges";
            const stretchText = stretch.items.map((item) => item.text).join("");
            const stretchCut = ChangeExcerpt.cutOf(stretchText, place, contextUnits);
            ChangeExcerpt.applyCut(document, stretch.items, stretchCut);
            ChangeExcerpt.placeParagraphGap(document, paragraph, stretchCut.gap);
        });
        ChangeExcerpt.removeEmptiedRunsAndRevisions(paragraph);
    }

    /**
     * CASES
     *   - "paragraphStart"   -> the gap opens the paragraph, after its properties
     *   - "paragraphEnd"     -> the gap closes the paragraph
     *   - an offset or null  -> nothing here: a gap between two changes stands inside the
     *                           stretch's text (`applyCut`), and no gap stands for nothing cut
     *
     * WHY at the paragraph's edge
     *   Text cut from the paragraph's start or end may have stood inside another
     *   author's revision. A gap left in its place would be drawn inside that
     *   revision, struck or underlined in its colour, as if the author had written "…".
     */
    private static placeParagraphGap(document: Document, paragraph: Element, gap: StretchCut["gap"]): void {
        if (gap === "paragraphStart") {
            const paragraphProperties = ChangeExcerpt.childNamed(paragraph, "w:pPr");
            paragraph.insertBefore(ChangeExcerpt.gapElement(document), paragraphProperties === null ? paragraph.firstChild : paragraphProperties.nextSibling);
            return;
        }
        if (gap === "paragraphEnd") paragraph.appendChild(ChangeExcerpt.gapElement(document));
    }

    /**
     * Removes every run and revision of the paragraph that the cut left with nothing
     * to draw, innermost first, so an earlier author's revision cut away whole leaves
     * no empty wrapper behind.
     *
     * CASES, per run, revision or hyperlink outside the paragraph's properties
     *   - it still holds text, a tab, a break or one of the excerpt's marks -> kept
     *   - it holds none                                                     -> removed: it draws nothing
     */
    private static removeEmptiedRunsAndRevisions(paragraph: Element): void {
        const wrappers = Array.from(paragraph.getElementsByTagName("*")).filter((element) => WRAPPERS_EMPTIED_BY_A_CUT.has(element.nodeName) && ChangeExcerpt.ancestorNamed(element, "w:pPr") === null);
        /**
         * WHY reversed
         *   Document order lists a wrapper before the wrappers inside it; read back to
         *   front, an inner wrapper is removed before its outer one is asked whether it
         *   still draws anything.
         */
        for (const wrapper of wrappers.reverse()) {
            if (!ChangeExcerpt.drawsAnything(wrapper)) wrapper.parentNode?.removeChild(wrapper);
        }
    }

    /**
     * CASES
     *   - one of the excerpt's own marks                         -> true
     *   - `w:tab`, `w:br` or `w:cr`                              -> true
     *   - holds a non-empty text node of `w:t`, `w:delText`, `m:t` -> true
     *   - a properties element or field instruction              -> false: drawn as nothing
     *   - anything else                                          -> whether any child draws anything
     */
    private static drawsAnything(node: Node): boolean {
        if (node.nodeType === TEXT_NODE_TYPE) return TEXT_BEARING_ELEMENTS.has(node.parentNode?.nodeName ?? "") && (node.nodeValue ?? "").length > 0;
        if (node.nodeType !== ELEMENT_NODE_TYPE) return false;
        const element = node as Element;
        if (element.namespaceURI === CHANGE_EXCERPT_NAMESPACE) return true;
        if (DRAWN_MARK_ELEMENTS.has(element.nodeName)) return true;
        if (SUBTREES_DRAWN_AS_NOTHING.has(element.nodeName)) return false;
        return Array.from(element.childNodes).some((child) => ChangeExcerpt.drawsAnything(child));
    }

    /** The items grouped into maximal runs that are all changes or all unchanged text, in reading order. */
    private static stretchesOf(items: DrawnItem[]): DrawnStretch[] {
        const stretches: DrawnStretch[] = [];
        for (const item of items) {
            const current = stretches[stretches.length - 1];
            /**
             * CASES
             *   - the item continues the current stretch (same change flag) -> joins it
             *   - it does not, or there is no stretch yet                 -> opens a new one
             */
            if (current !== undefined && current.changed === item.changed) current.items.push(item);
            else stretches.push({ items: [item], changed: item.changed });
        }
        return stretches;
    }

    /**
     * What is kept of one unchanged stretch.
     *
     * CASES
     *   - before the first change, at most `contextUnits` units -> all of it
     *   - before the first change, more                          -> a gap, then the stretch from the start
     *                                                               of its `contextUnits`-th unit from the end
     *   - after the last change, at most `contextUnits` units   -> all of it
     *   - after the last change, more                            -> the stretch up to the end of its
     *                                                               `contextUnits`-th unit, then a gap
     *   - between changes, at most twice `contextUnits` units   -> all of it: the two changes read as one stretch
     *   - between changes, more                                  -> both ends of `contextUnits` units, a gap between
     *
     * EXAMPLE, contextUnits 2, after the last change
     *   stretch " days of receipt."  units "days" 1..5, "of" 6..8, "receipt." 9..18
     *   the 2nd unit ends at 8 -> kept [0, 8) " days of", gap at 8
     */
    private static cutOf(stretchText: string, place: StretchPlace, contextUnits: number): StretchCut {
        const units = WordTokenizer.diffUnits(stretchText);
        const whole: StretchCut = { keptRanges: [{ startOffset: 0, endOffset: stretchText.length }], gap: null };
        /**
         * EXAMPLE
         *   units "a" 0..1, "b" 2..3, "c" 4..5, contextUnits 2
         *   the last 2 units begin at units[3 - 2] = units[1] -> offset 2
         *   contextUnits 0 -> nothing of the stretch is kept: the end of the stretch
         */
        const startOfLastUnits = (count: number): number => (count === 0 ? stretchText.length : units[units.length - count].startOffset);
        /**
         * EXAMPLE
         *   units "a" 0..1, "b" 2..3, "c" 4..5, contextUnits 2
         *   the first 2 units end where units[2 - 1] = units[1] ends -> offset 3
         *   contextUnits 0 -> nothing of the stretch is kept: offset 0
         */
        const endOfFirstUnits = (count: number): number => (count === 0 ? 0 : units[count - 1].endOffset);
        if (place === "beforeFirstChange") {
            if (units.length <= contextUnits) return whole;
            return { keptRanges: [{ startOffset: startOfLastUnits(contextUnits), endOffset: stretchText.length }], gap: "paragraphStart" };
        }
        if (place === "afterLastChange") {
            if (units.length <= contextUnits) return whole;
            return { keptRanges: [{ startOffset: 0, endOffset: endOfFirstUnits(contextUnits) }], gap: "paragraphEnd" };
        }
        /**
         * WHEN the stretch between two changes is short
         *   CONSEQUENCE
         *     kept whole: cutting it would leave a gap mark between words that all fit.
         */
        if (units.length <= 2 * contextUnits) return whole;
        const keptEnd = endOfFirstUnits(contextUnits);
        return {
            keptRanges: [
                { startOffset: 0, endOffset: keptEnd },
                { startOffset: startOfLastUnits(contextUnits), endOffset: stretchText.length },
            ],
            gap: keptEnd,
        };
    }

    /**
     * Writes a stretch's cut into its nodes.
     *
     * CASES, per item, at its offsets `[itemStart, itemEnd)` in the stretch's text
     *   - a text node                    -> its kept characters, with the gap element where a gap
     *                                       between two changes falls inside it; removed when
     *                                       nothing of it is kept
     *   - a tab or line break element    -> kept when its one character is kept, else removed; a gap
     *                                       between two changes at its place stands just before it
     *
     * EXAMPLE
     *   items      text "pay within thirty days and " 0..27, tab 27..28, text "in any event within " 28..48
     *   units      9: "pay" 0..3, "within" 4..10, … "event" 35..40, "within" 41..47
     *   kept       [0, 10) and [35, 48), the gap at 10 (between two changes, contextUnits 2)
     *   result     "pay within", the gap element, then "event within "; the tab is removed
     */
    private static applyCut(document: Document, items: DrawnItem[], stretchCut: StretchCut): void {
        const gapOffset = typeof stretchCut.gap === "number" ? stretchCut.gap : null;
        let itemStart = 0;
        for (const item of items) {
            /**
             * EXAMPLE
             *   itemStart 27, item "\t" (length 1) -> itemEnd 28, the next item starts at 28
             */
            const itemEnd = itemStart + item.text.length;
            const gapOffsetsHere = gapOffset !== null && itemStart <= gapOffset && gapOffset < itemEnd ? [gapOffset] : [];
            const keptRangesHere = stretchCut.keptRanges
                .map((range) => ({ startOffset: Math.max(range.startOffset, itemStart), endOffset: Math.min(range.endOffset, itemEnd) }))
                .filter((range) => range.startOffset < range.endOffset);
            if (item.isMark) ChangeExcerpt.applyCutToMark(document, item, gapOffsetsHere.length > 0, keptRangesHere.length > 0);
            else ChangeExcerpt.applyCutToText(document, item, itemStart, keptRangesHere, gapOffsetsHere);
            itemStart = itemEnd;
        }
    }

    /**
     * CASES
     *   - a gap falls at the mark          -> the gap element goes just before it
     *   - the mark's character is kept     -> the mark stays
     *   - it is not                        -> the mark is removed
     */
    private static applyCutToMark(document: Document, item: DrawnItem, gapStandsHere: boolean, markIsKept: boolean): void {
        const parent = item.node.parentNode;
        /**
         * NULL
         *   A mark with no parent was already taken out of the document; there is nothing to write.
         */
        if (parent === null) return;
        if (gapStandsHere) parent.insertBefore(ChangeExcerpt.gapElement(document), item.node);
        if (!markIsKept) parent.removeChild(item.node);
    }

    /**
     * The text node replaced by its kept characters and any gap inside it, in reading order.
     *
     * EXAMPLE
     *   item "pay within thirty" at 10..27, kept [10, 21) and a gap at 21
     *   local kept [0, 11) "pay within ", local gap 11 -> Text "pay within ", gap element
     *
     * CASES
     *   - the whole node is kept and no gap falls in it -> left as it is
     *   - otherwise                                       -> replaced by the pieces; removed when there are none
     */
    private static applyCutToText(document: Document, item: DrawnItem, itemStart: number, keptRanges: KeptRange[], gapOffsets: number[]): void {
        const keptWhole = gapOffsets.length === 0 && keptRanges.length === 1 && keptRanges[0].startOffset === itemStart && keptRanges[0].endOffset === itemStart + item.text.length;
        if (keptWhole) return;
        const parent = item.node.parentNode;
        /**
         * NULL
         *   A text node with no parent was already taken out of the document; there is nothing to write.
         */
        if (parent === null) return;
        const pieces = [
            ...keptRanges.map((range) => ({ offset: range.startOffset, node: document.createTextNode(item.text.slice(range.startOffset - itemStart, range.endOffset - itemStart)) as Node })),
            ...gapOffsets.map((gapOffset) => ({ offset: gapOffset, node: ChangeExcerpt.gapElement(document) as Node })),
        ].sort((left, right) => left.offset - right.offset);
        for (const piece of pieces) parent.insertBefore(piece.node, item.node);
        parent.removeChild(item.node);
    }

    private static gapElement(document: Document): Element {
        return document.createElementNS(CHANGE_EXCERPT_NAMESPACE, CHANGE_EXCERPT_GAP);
    }

    /**
     * CASES, per paragraph
     *   - it holds a change           -> kept
     *   - it stands in a table cell   -> kept here; its row decides (`removeTablesAndRowsWithoutChanges`)
     *   - otherwise                   -> removed
     */
    private static removeParagraphsOutsideTables(paragraphs: Element[], paragraphsHoldingChanges: Set<Element>): void {
        for (const paragraph of paragraphs) {
            if (paragraphsHoldingChanges.has(paragraph)) continue;
            if (ChangeExcerpt.ancestorNamed(paragraph, "w:tc") !== null) continue;
            paragraph.parentNode?.removeChild(paragraph);
        }
    }

    /**
     * CASES, per table, nested ones included
     *   - none of its paragraphs holds a change -> the table is removed
     *   - otherwise                             -> each of its own rows holding no change is removed
     */
    private static removeTablesAndRowsWithoutChanges(root: Element, paragraphsHoldingChanges: Set<Element>): void {
        const holdsChange = (element: Element): boolean => Array.from(element.getElementsByTagName("w:p")).some((paragraph) => paragraphsHoldingChanges.has(paragraph));
        for (const table of Array.from(root.getElementsByTagName("w:tbl"))) {
            /**
             * WHEN an enclosing table or row already went
             *   CONSEQUENCE
             *     nothing to do: this table went with it.
             */
            if (!ChangeExcerpt.standsUnder(table, root)) continue;
            if (!holdsChange(table)) {
                table.parentNode?.removeChild(table);
                continue;
            }
            for (const row of Array.from(table.childNodes)) {
                if (row.nodeType !== ELEMENT_NODE_TYPE || row.nodeName !== "w:tr") continue;
                if (!holdsChange(row as Element)) table.removeChild(row);
            }
        }
    }

    /** True when `element` is still inside `root`: no ancestor of it was taken out of the document. */
    private static standsUnder(element: Element, root: Element): boolean {
        for (let ancestor = element.parentNode; ancestor !== null; ancestor = ancestor.parentNode) {
            if (ancestor === root) return true;
        }
        return false;
    }

    /**
     * Leaves out every block after the first `blockLimit`, and stands a "more"
     * mark counting them after the last block kept.
     *
     * A block is a paragraph or a table that no other paragraph or table holds:
     * one thing the excerpt draws as a unit.
     *
     * EXAMPLE
     *   blocks [p1, p2, table, p3], blockLimit 2
     *   kept [p1, p2]; table and p3 removed; the mark after p2 counts 4 - 2 = 2
     *
     * CASES
     *   - at most `blockLimit` blocks -> nothing is left out
     *   - `blockLimit` 0              -> every block is left out; the mark stands where the first one stood
     */
    private static limitBlocks(document: Document, root: Element, blockLimit: number): void {
        const blocks = Array.from(root.getElementsByTagName("*")).filter((element) => (element.nodeName === "w:p" || element.nodeName === "w:tbl") && !ChangeExcerpt.standsInBlock(element, root));
        if (blocks.length <= blockLimit) return;
        const moreMark = document.createElementNS(CHANGE_EXCERPT_NAMESPACE, CHANGE_EXCERPT_MORE);
        moreMark.setAttributeNS(CHANGE_EXCERPT_NAMESPACE, CHANGE_EXCERPT_MORE_COUNT, String(blocks.length - blockLimit));
        /**
         * EXAMPLE
         *   blockLimit 2 -> the mark goes after blocks[2 - 1] = blocks[1], the last block kept
         *   blockLimit 0 -> no block is kept: the mark goes before blocks[0], where the first one stood
         */
        const lastKeptBlock = blockLimit === 0 ? null : blocks[blockLimit - 1];
        if (lastKeptBlock === null) blocks[0].parentNode?.insertBefore(moreMark, blocks[0]);
        else lastKeptBlock.parentNode?.insertBefore(moreMark, lastKeptBlock.nextSibling);
        for (const block of blocks.slice(blockLimit)) block.parentNode?.removeChild(block);
    }

    /** True when a paragraph or table encloses `element` below `root`. */
    private static standsInBlock(element: Element, root: Element): boolean {
        for (let ancestor = element.parentNode; ancestor !== null && ancestor !== root; ancestor = ancestor.parentNode) {
            if (ancestor.nodeName === "w:p" || ancestor.nodeName === "w:tbl") return true;
        }
        return false;
    }

    /** The nearest enclosing node with this qualified name, or null. */
    private static ancestorNamed(element: Element, qualifiedName: string): Node | null {
        for (let ancestor = element.parentNode; ancestor !== null; ancestor = ancestor.parentNode) {
            if (ancestor.nodeName === qualifiedName) return ancestor;
        }
        return null;
    }
}
